/**
 * Shared React Context instance and types for JWT-based authentication.
 */

import { createContext } from 'react';

export interface SignUpData {
  email: string;
  password: string;
  firstName: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  company?: string;
  emailVerified?: boolean;
  isPlatformAdmin?: boolean;
}

export interface AuthError {
  code: string;
  message: string;
  field?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface SignUpResult {
  requiresVerification: boolean;
  message?: string;
  user?: User;
}

export interface JWTAuthContextType {
  // Core state
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isInitialized: boolean; // True once initial auth check completes (prevents race conditions)

  // Authentication methods - onComplete callback is called after user state is set
  signIn: (email: string, password: string, onComplete?: (user: User) => void) => Promise<void>;
  signUp: (userData: SignUpData, onComplete?: (user: User) => void) => Promise<SignUpResult>;
  signOut: () => Promise<void>;

  // Error handling
  error: AuthError | null;
  clearError: () => void;
}

export const JWTAuthContext = createContext<JWTAuthContextType | undefined>(undefined);
